import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { useAuth } from './AuthContext';

export interface Widget {
    id: number;
    type: 'kpi' | 'chart' | 'table';
    category: string;
    key: string;
    title: string;
    description?: string;
    icon?: string;
    config?: any;
    is_active: boolean;
    sort_order: number;
}

export interface MerchantWidget extends Widget {
    pivot: {
        settings: any;
        is_enabled: boolean;
        position: number;
        is_initial_selection?: boolean;
        has_prediction?: boolean;
        is_initial_prediction?: boolean;
        prediction_selected_at?: string;
    };
}

export interface WidgetData {
    widget: Widget;
    data: any;
    cached_at?: string;
    from_cache: boolean;
}

interface WidgetContextType {
    availableWidgets: Record<string, Widget[]>;
    merchantWidgets: Record<string, MerchantWidget[]>;
    widgetCategories: string[];
    isLoading: boolean;
    error: string | null;
    loadAvailableWidgets: () => Promise<void>;
    loadMerchantWidgets: (shop: string) => Promise<void>;
    updateMerchantWidgets: (shop: string, widgets: any[]) => Promise<void>;
    getWidgetData: (shop: string, widgetKey: string, filters?: any) => Promise<WidgetData>;
    refreshWidgetData: (shop: string, widgetKey: string, filters?: any) => Promise<WidgetData>;
    getUniversalWidgetData: (shop: string, widgetKeys: string[], filters?: any) => Promise<any>;
}

// Priority cards that should appear at the top of default dashboard sight
const PRIORITY_WIDGET_KEYS = [
    'total_sales',      // Total Sales
    'total_orders',      // Total Orders  
    'average_order_value', // Average Order
    'blended_roas',     // Blended ROAS
    'blended_cac',      // Blended CAC
    'blended_cpo',      // Blended CPO
    'blended_cac_aov_ratio', // Blended CAC:AOV Ratio
    'blended_sessions',  // Blended Sessions
    'meta_spend_trend', // Ads Spend
    'ga4_sessions',     // Sessions
    'meta_mer'          // Blended MER
] as const;

const DEFAULT_WIDGET_KEYS = [
    'total_sales', 
    'total_orders', 
    'average_order_value',
    'blended_roas', 
    'blended_cac',
    'blended_cpo',
    'blended_cac_aov_ratio',
    'blended_sessions',
    'meta_spend_trend',
    'ga4_sessions',
    'meta_mer'
] as const;

const WidgetContext = createContext<WidgetContextType | undefined>(undefined);

export const useWidgets = () => {
    const context = useContext(WidgetContext);
    if (!context) {
        throw new Error('useWidgets must be used within a WidgetProvider');
    }
    return context;
};

interface WidgetProviderProps {
    children: ReactNode;
}

export const WidgetProvider: React.FC<WidgetProviderProps> = ({ children }) => {
    const { user, token } = useAuth();
    const [availableWidgets, setAvailableWidgets] = useState<Record<string, Widget[]>>({});
    const [merchantWidgets, setMerchantWidgets] = useState<Record<string, MerchantWidget[]>>({});
    const [widgetCategories, setWidgetCategories] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // CRITICAL: Helper function to check if we're on billing page
    // Check both pathname and full URL to handle different formats
    const isBillingPage = () => {
        if (typeof window === 'undefined') return false;
        const pathname = window.location.pathname;
        const fullPath = window.location.pathname + window.location.search;
        return pathname === '/billing/choose-plan' || 
               pathname.includes('/billing/choose-plan') ||
               fullPath.includes('/billing/choose-plan');
    };
    
    // Check if we're on a billing page - provide context but skip auto-loading
    const isOnBillingPage = isBillingPage();

    // Helper function to check connection status
    const checkConnectionStatus = async () => {
        try {
            const [fbResult, ga4Result, googleResult] = await Promise.allSettled([
                apiGet('/facebook/account'),
                apiGet('/ga4/account'),
                apiGet('/googleAd/account'),
            ]);

            const status = {
                meta: false,
                ga4: false,
                google_ads: false,
            };

            if (fbResult.status === 'fulfilled' && (fbResult.value as any)?.account) {
                status.meta = true;
            }

            if (ga4Result.status === 'fulfilled' && (ga4Result.value as any)?.account) {
                status.ga4 = true;
            }

            if (
                googleResult.status === 'fulfilled' &&
                ((googleResult.value as any)?.accounts?.length ?? 0) > 0
            ) {
                status.google_ads = true;
            }

            return status;
        } catch (error) {
            console.error('Failed to check connection status:', error);
            return {
                meta: false,
                ga4: false,
                google_ads: false,
            };
        }
    };

    // Helper function to get connection type from widget
    const getWidgetConnectionType = (widget: Widget): 'shopify' | 'meta' | 'ga4' | 'google_ads' => {
        const category = (widget.category || '').toLowerCase();
        const key = (widget.key || '').toLowerCase();
        const title = (widget.title || '').toLowerCase();
        const text = `${category} ${key} ${title}`;

        if (text.includes('meta') || text.includes('facebook')) {
            return 'meta';
        }
        if (text.includes('ga4') || text.includes('analytics')) {
            return 'ga4';
        }
        if (text.includes('google_ads') || text.includes('googleads') || text.includes('google ads')) {
            return 'google_ads';
        }
        return 'shopify';
    };

    const loadAvailableWidgets = useCallback(async () => {
        // Don't load if on billing page - check immediately
        if (isOnBillingPage) {
            setIsLoading(false);
            return;
        }
        
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiGet<{
                widgets: Record<string, Widget[]>;
                categories: string[];
                plan_info?: {
                    widget_limit?: number;
                    available_connections?: string[];
                    connection?: number;
                };
            }>('/widgets/available');

            setAvailableWidgets(response.widgets);
            setWidgetCategories(response.categories);
            
            // Store plan_info in context if needed (can be accessed via response)
            // For now, WidgetManager will fetch it separately
        } catch (err: any) {
            // CRITICAL: Don't log errors if on billing page, if it's a 402 (Payment Required), or if it's blocked
            // 402 errors are expected when there's no subscription
            const is402Error = err?.response?.status === 402 || err?.status === 402 || err?.message?.includes('402');
            const isBlocked = err?.message?.includes('API call blocked');
            
            if (!isBillingPage() && !is402Error && !isBlocked) {
            setError('Failed to load available widgets');
            console.error('Error loading available widgets:', err);
            }
            // If 402, blocked, or on billing page, silently fail - don't set error
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Don't load widgets if we're on the billing page
        // This prevents unnecessary API calls and 402 errors that cause re-renders
        if (isOnBillingPage) {
            // On billing page - don't load anything, exit immediately
            setIsLoading(false);
            return;
        }
        
        // Only load if we don't have widgets, user is authenticated, and we're not on billing page
        if (Object.keys(availableWidgets).length === 0 && user && token) {
            loadAvailableWidgets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadAvailableWidgets, user, token]); // Depend on auth state too

    const loadMerchantWidgets = useCallback(async (shop: string) => {
        // Don't load widgets if on billing page or no shop
        if (isOnBillingPage || !shop) {
            return;
        }
        
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiGet<{
                widgets: Record<string, MerchantWidget[]>;
                categories: string[];
            }>('/widgets/merchant', { shop });

            const currentWidgets = response.widgets ?? {};
            const hasEnabledWidgets = Object.values(currentWidgets).some((list) =>
                list.some((widget) => widget.pivot?.is_enabled)
            );

            if (!hasEnabledWidgets) {
                // Gather available widgets so we can seed defaults
                let catalog = Object.values(availableWidgets).flat();

                if (catalog.length === 0) {
                    try {
                        const availableResponse = await apiGet<{
                            widgets: Record<string, Widget[]>;
                            categories: string[];
                        }>('/widgets/available');

                        catalog = Object.values(availableResponse.widgets ?? {}).flat();
                        setAvailableWidgets(availableResponse.widgets ?? {});
                        setWidgetCategories(availableResponse.categories ?? []);
                    } catch (catalogError: any) {
                        // CRITICAL: Don't log if blocked or on billing page
                        const isBillingPageCheck = isBillingPage();
                        const isBlocked = catalogError?.message?.includes('API call blocked');
                        if (!isBillingPageCheck && !isBlocked) {
                        console.error('Unable to load widget catalog for defaults:', catalogError);
                        }
                    }
                }

                // Check connection status before adding default widgets
                const connectionStatus = await checkConnectionStatus();
                
                // Create priority widgets first (in order), then other widgets
                const priorityWidgets = PRIORITY_WIDGET_KEYS.map((key, index) => {
                    const widget = catalog.find((item) => item.key === key);
                    if (!widget) {
                        return null;
                    }

                    // Check if this widget requires a specific connection
                    const requiredConnection = getWidgetConnectionType(widget);
                    const isConnectionActive = requiredConnection === 'shopify' || 
                        (requiredConnection === 'meta' && connectionStatus.meta) ||
                        (requiredConnection === 'ga4' && connectionStatus.ga4) ||
                        (requiredConnection === 'google_ads' && connectionStatus.google_ads);

                    // Skip widget if required connection is not active
                    if (!isConnectionActive) {
                        return null;
                    }

                    const settings = {
                        width: 3,
                        height: 2,
                        x: (index % 4) * 3,
                        y: Math.floor(index / 4) * 2,
                    };

                    return {
                        ...widget,
                        pivot: {
                            settings,
                            is_enabled: true,
                            position: index,
                        },
                    } as MerchantWidget;
                }).filter(Boolean) as MerchantWidget[];

                // Create other widgets (non-priority) after priority ones
                const otherWidgetKeys = DEFAULT_WIDGET_KEYS.filter(key => !PRIORITY_WIDGET_KEYS.includes(key));
                const otherWidgets = otherWidgetKeys.map((key, index) => {
                    const widget = catalog.find((item) => item.key === key);
                    if (!widget) {
                        return null;
                    }

                    // Check if this widget requires a specific connection
                    const requiredConnection = getWidgetConnectionType(widget);
                    const isConnectionActive = requiredConnection === 'shopify' || 
                        (requiredConnection === 'meta' && connectionStatus.meta) ||
                        (requiredConnection === 'ga4' && connectionStatus.ga4) ||
                        (requiredConnection === 'google_ads' && connectionStatus.google_ads);

                    // Skip widget if required connection is not active
                    if (!isConnectionActive) {
                        return null;
                    }

                    const settings = {
                        width: 3,
                        height: 2,
                        x: (index % 4) * 3,
                        y: Math.floor(index / 4) * 2,
                    };

                    return {
                        ...widget,
                        pivot: {
                            settings,
                            is_enabled: true,
                            position: index + PRIORITY_WIDGET_KEYS.length, // Position after priority widgets
                        },
                    } as MerchantWidget;
                }).filter(Boolean) as MerchantWidget[];

                // Combine priority widgets first, then other widgets
                const defaultWidgets = [...priorityWidgets, ...otherWidgets];

                if (defaultWidgets.length > 0) {
                    const groupedDefaults = defaultWidgets.reduce<Record<string, MerchantWidget[]>>((acc, widget) => {
                        const category = widget.category || 'default';
                        if (!acc[category]) {
                            acc[category] = [];
                        }
                        acc[category].push(widget);
                        return acc;
                    }, {});

                    const mergedWidgets: Record<string, MerchantWidget[]> = {
                        ...currentWidgets,
                        ...groupedDefaults,
                    };

                    setMerchantWidgets(prev => ({
                        ...prev,
                        [shop]: mergedWidgets as Record<string, MerchantWidget[]>,
                    }));

                    // Create default dashboard sight with priority ordering
                    try {
                        const sightSlug = `dashboard-${Date.now().toString(36)}${Math.random().toString(36).substr(2, 9)}`;
                        
                        // Group widgets by category for sight creation
                        const widgetsByCategory: Record<string, any[]> = {};
                        
                        // Sort default widgets by priority first, then group by category
                        const sortedWidgets = [...defaultWidgets].sort((a, b) => {
                            const aPriority = PRIORITY_WIDGET_KEYS.indexOf(a.key as any);
                            const bPriority = PRIORITY_WIDGET_KEYS.indexOf(b.key as any);
                            
                            // If both are priority cards, maintain priority order
                            if (aPriority !== -1 && bPriority !== -1) {
                                return aPriority - bPriority;
                            }
                            
                            // Priority cards come first
                            if (aPriority !== -1) return -1;
                            if (bPriority !== -1) return 1;
                            
                            // Non-priority cards maintain original order
                            return 0;
                        });
                        
                        sortedWidgets.forEach(widget => {
                            const category = widget.category || 'shopify';
                            if (!widgetsByCategory[category]) {
                                widgetsByCategory[category] = [];
                            }
                            widgetsByCategory[category].push({
                                widget_id: widget.id,
                                is_enabled: true,
                                position: widget.pivot?.position ?? 0,
                                settings: widget.pivot?.settings ?? { width: 3, height: 2 },
                                is_initial_selection: true
                            });
                        });

                        const sightData = {
                            name: 'Default Dashboard',
                            slug: sightSlug,
                            icon: 'dashboard',
                            description: 'Default dashboard with priority cards on top',
                            widgets: widgetsByCategory
                        };

                        await apiPost('/sights', {
                            shop,
                            ...sightData
                        });

                        console.log('Created default dashboard sight:', sightSlug, 'with widgets:', widgetsByCategory);
                    } catch (sightError) {
                        console.error('Failed to create default sight:', sightError);
                    }

                    try {
                        await apiPost('/widgets/merchant', {
                            shop,
                            widgets: defaultWidgets.map((widget) => ({
                                widget_id: widget.id,
                                is_enabled: true,
                                position: widget.pivot?.position ?? 0,
                                settings: widget.pivot?.settings ?? { width: 3, height: 2 },
                                is_initial_selection: true, // Mark default widgets as initial selection
                            })),
                        });
                    } catch (persistError) {
                        console.error('Failed to persist default widgets:', persistError);
                    }

                    return;
                }
            }

            setMerchantWidgets(prev => ({
                ...prev,
                [shop]: currentWidgets as Record<string, MerchantWidget[]>,
            }));
        } catch (err: any) {
            // CRITICAL: Don't log errors if on billing page, if it's a 402 (Payment Required), or if it's blocked
            const isBillingPageCheck = isBillingPage();
            const is402Error = err?.response?.status === 402 || err?.status === 402 || err?.message?.includes('402');
            const isBlocked = err?.message?.includes('API call blocked');
            
            if (!isBillingPageCheck && !is402Error && !isBlocked) {
            setError('Failed to load merchant widgets');
            console.error('Error loading merchant widgets:', err);
            }
            // If 402, blocked, or on billing page, silently fail - don't set error
        } finally {
            setIsLoading(false);
        }
    }, [availableWidgets]);

    const updateMerchantWidgets = useCallback(async (shop: string, widgets: any[]): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);
            await apiPost('/widgets/merchant', {
                shop,
                widgets
            });

            // Always reload merchant widgets after update to ensure we have correct grouped format
            // This ensures dashboard gets the updated widgets in the expected format
            await loadMerchantWidgets(shop);
            
            return; // Explicitly return void
        } catch (err) {
            setError('Failed to update merchant widgets');
            console.error('Error updating merchant widgets:', err);
            throw err; // Re-throw so caller can handle it
        } finally {
            setIsLoading(false);
        }
    }, [loadMerchantWidgets]);

    const getWidgetData = useCallback(async (shop: string, widgetKey: string, filters: any = {}) => {
        try {
            setError(null);
            const response = await apiGet<WidgetData>('/widgets/data', {
                shop,
                widget_key: widgetKey,
                ...filters
            });

            return response;
        } catch (err) {
            setError(`Failed to load data for widget ${widgetKey}`);
            console.error('Error loading widget data:', err);
            throw err;
        }
    }, []);

    const getUniversalWidgetData = useCallback(async (shop: string, widgetKeys: string[], filters: any = {}) => {
        try {
            setError(null);
            const response = await apiGet<any>('/kpi/netsight/universal', {
                shop,
                widget_key: widgetKeys.join(','),
                ...filters
            });

            return response;
        } catch (err) {
            setError(`Failed to load universal data for widgets: ${widgetKeys.join(',')}`);
            console.error('Error loading universal widget data:', err);
            throw err;
        }
    }, []);

    const refreshWidgetData = useCallback(async (shop: string, widgetKey: string, filters: any = {}) => {
        // Force refresh by adding a timestamp to bypass cache
        return getWidgetData(shop, widgetKey, { ...filters, _refresh: Date.now() });
    }, [getWidgetData]);

    const value: WidgetContextType = {
        availableWidgets,
        merchantWidgets,
        widgetCategories,
        isLoading,
        error,
        loadAvailableWidgets,
        loadMerchantWidgets,
        updateMerchantWidgets,
        getWidgetData,
        refreshWidgetData,
        getUniversalWidgetData,
    };

    return (
        <WidgetContext.Provider value={value}>
            {children}
        </WidgetContext.Provider>
    );
};
